import React from 'react';
import { motion } from 'framer-motion';
import { CalendarPlus, MessageSquareText, ChartLine, LucideIcon } from 'lucide-react';
import { AI_ASSISTANT, QUICK_SUGGESTIONS } from '@/lib/ai-chat/constants';
import { cn } from '@/lib/core/utils';

// Map icon names to components
const iconMap: Record<string, LucideIcon> = {
  CalendarPlus,
  MessageSquareText,
  ChartLine,
};

interface WelcomeScreenProps {
  onSuggestionClick: (text: string) => void;
}

export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ onSuggestionClick }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col items-center px-6 pt-12"
    >
      {/* AI Avatar */}
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        className="w-14 h-14 rounded-full flex items-center justify-center ring-1 ring-border mb-4 bg-background"
      >
        <img
          src="/media/tag-innovation-logo.png"
          alt={AI_ASSISTANT.name}
          className="h-8 w-8 rounded-sm"
        />
      </motion.div>

      {/* Heading */}
      <motion.h2
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="text-xl font-semibold text-foreground mb-4"
      >
        How can I help you today?
      </motion.h2>

      {/* Suggestion chips */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="w-full max-w-sm space-y-2"
      >
        {QUICK_SUGGESTIONS.map((suggestion, index) => {
          const IconComponent = iconMap[suggestion.icon];
          return (
            <motion.button
              key={suggestion.text}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.08 }}
              onClick={() => onSuggestionClick(suggestion.text)}
              className={cn(
                'w-full flex items-center gap-3 px-4 py-3 rounded-xl',
                'bg-muted/50 hover:bg-muted',
                'text-left text-sm',
                'transition-colors duration-150'
              )}
            >
              {IconComponent && (
                <IconComponent className="h-5 w-5 text-muted-foreground shrink-0" />
              )}
              <span>{suggestion.text}</span>
            </motion.button>
          );
        })}
      </motion.div>

      {/* Tagline */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-xs text-muted-foreground/50 mt-4 text-center"
      >
        {AI_ASSISTANT.tagline}
      </motion.p>
    </motion.div>
  );
};

export default WelcomeScreen;
